<template>
    <transition name="modal-fade">
        <div v-if="visible" class="settings-modal-overlay" @click.self="handleClose">
            <div class="settings-modal-container">
                <!-- 헤더 -->
                <div class="settings-modal-header">
                    <div class="header-title">
                        <i class="fas fa-cog"></i>
                        <span>설정</span>
                    </div>
                    <button class="close-btn" @click="handleClose" title="닫기">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <!-- 컨텐츠 -->
                <div class="settings-modal-content">
                    <!-- 좌측 메뉴 -->
                    <div class="settings-sidebar">
                        <nav class="settings-nav">
                            <template v-for="(group, groupIndex) in settingsMenuGroups">
                                <div
                                    v-if="group.title"
                                    :key="`group-header-${groupIndex}`"
                                    class="settings-nav-group-header"
                                >
                                    {{ group.title }}
                                </div>
                                <button
                                    v-for="menu in group.menus"
                                    :key="menu.key"
                                    class="settings-nav-item"
                                    :class="{ active: activeMenu === menu.key }"
                                    @click="handleNavClick(menu.key)"
                                >
                                    <i :class="menu.icon"></i>
                                    <span>{{ menu.label }}</span>
                                </button>
                                <div
                                    v-if="groupIndex < settingsMenuGroups.length - 1"
                                    :key="`divider-${groupIndex}`"
                                    class="settings-nav-divider"
                                ></div>
                            </template>
                        </nav>
                    </div>

                    <!-- 우측 컨텐츠 -->
                    <div class="settings-main">
                        <!-- 기준정보 설정 -->
                        <div v-if="activeMenu === 'standard'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">기준정보 설정</h3>
                                <p class="panel-desc">기준정보를 설정합니다</p>
                            </div>
                            <div class="panel-body">
                                <StandardSettingsPanel :loginUser="loginUser" />
                            </div>
                        </div>
                        <!-- 경계 설정 -->
                        <div v-if="activeMenu === 'boundary'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">배출경계 설정</h3>
                                <p class="panel-desc">
                                    조직별로 조직경계(Organizational Boundary)와 운영경계(Operational Boundary)를
                                    설정합니다.
                                </p>
                            </div>
                            <div class="panel-body">
                                <BoundaryWizard :loginUser="loginUser" @boundary-saved="handleBoundarySaved" />
                            </div>
                        </div>

                        <!-- 활동자료 (목록만) -->
                        <div v-if="activeMenu === 'activity'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">활동자료 조회</h3>
                                <p class="panel-desc">배출 활동 데이터 목록을 조회합니다</p>
                            </div>
                            <div class="panel-body">
                                <ActivityDataViewer :loginUser="loginUser" />
                            </div>
                        </div>

                        <!-- 목표 설정 -->
                        <div v-if="activeMenu === 'target'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">배출목표 설정</h3>
                                <p class="panel-desc">감축 목표 및 로드맵을 수립합니다</p>
                            </div>
                            <div class="panel-body">
                                <TargetMain :is-active="activeMenu === 'target'" @target-saved="handleTargetSaved" />
                            </div>
                        </div>

                        <!-- 배출계수 관리 -->
                        <div v-if="activeMenu === 'factor'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">배출계수 설정</h3>
                                <p class="panel-desc">배출계수를 관리합니다</p>
                            </div>
                            <div class="panel-body">
                                <FactorMain :loginUser="loginUser" />
                            </div>
                        </div>

                        <!-- 권한 관리 -->
                        <div v-if="activeMenu === 'access'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">권한 관리</h3>
                                <p class="panel-desc">접근 제어 및 권한을 관리합니다</p>
                            </div>
                            <div class="panel-body">
                                <AccessControlManager ref="accessControlRef" :loginUser="loginUser" />
                            </div>
                        </div>

                        <!-- 수집 설정 -->
                        <div v-if="activeMenu === 'config'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">배출원 설정</h3>
                                <p class="panel-desc">활동 데이터 수집 설정을 관리합니다</p>
                            </div>
                            <div class="panel-body">
                                <ActivityConfigManager />
                            </div>
                        </div>

                        <!-- 승인 관리 -->
                        <div v-if="activeMenu === 'workflow'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">승인 관리</h3>
                                <p class="panel-desc">승인 워크플로우를 관리합니다</p>
                            </div>
                            <div class="panel-body">
                                <WorkflowManager :loginUser="loginUser" />
                            </div>
                        </div>

                        <!-- 알림 -->
                        <div v-if="activeMenu === 'notification'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">알림</h3>
                                <p class="panel-desc">워크플로우, 계산 실패, 마감 임박 등 알림 내역을 조회합니다</p>
                            </div>
                            <div class="panel-body">
                                <NotificationMain :loginUser="loginUser" />
                            </div>
                        </div>

                        <!-- 감사 로그 -->
                        <div v-if="activeMenu === 'auditLog'" class="settings-panel">
                            <div class="panel-header">
                                <h3 class="panel-title">감사 로그</h3>
                                <p class="panel-desc">
                                    주요 엔티티 변경 이력을 조회합니다 (Who/When/What/How/Why/Where)
                                </p>
                            </div>
                            <div class="panel-body">
                                <AuditLogMain :loginUser="loginUser" />
                            </div>
                        </div>

                        <!-- 산정 워크플로우 -->
                        <div v-if="activeMenu === 'calcWorkflow'" class="settings-panel">
                            <div class="panel-header panel-header-with-action">
                                <div class="panel-header-left">
                                    <h3 class="panel-title">산정 워크플로우</h3>
                                    <p class="panel-desc">산정 워크플로우를 관리합니다</p>
                                </div>
                                <button class="btn-create-workflow" @click="openCalcWorkflowCreate">
                                    <i class="fas fa-plus"></i>
                                    신규 생성
                                </button>
                            </div>
                            <div class="panel-body">
                                <CalcWorkflowMain ref="calcWorkflowMain" :loginUser="loginUser" />
                            </div>
                        </div>

                        <!-- 산정 인벤토리 결과 -->
                        <div v-if="activeMenu === 'emissionCalcResult'" class="settings-panel">
                            <div class="panel-header panel-header-with-action">
                                <div class="panel-header-left">
                                    <h3 class="panel-title">배출량 산정 내역</h3>
                                    <p class="panel-desc">월별/연도별 배출량 산정 내역을 조회합니다</p>
                                </div>
                                <CarbonButton
                                    icon="fas fa-file-excel"
                                    :disabled="!emissionCalcResultHasItems"
                                    @click="handleEmissionCalcResultExportExcel"
                                >
                                    엑셀
                                </CarbonButton>
                            </div>
                            <div class="panel-body">
                                <EmissionCalcResultPanel
                                    ref="emissionCalcResultPanelRef"
                                    :loginUser="loginUser"
                                    @items-changed="onEmissionCalcResultItemsChanged"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import BoundaryWizard from "../boundary/containers/BoundaryMain.vue";
import ActivityDataViewer from "../activity/containers/ActivityDataViewer.vue";
import TargetMain from "../target/containers/TargetMain.vue";
import FactorMain from "../factor/containers/FactorMain.vue";
import AccessControlManager from "../access/containers/AccessControlManager.vue";
import ActivityConfigManager from "../config/containers/ActivityConfigManager.vue";
import WorkflowManager from "../workflow/WorkflowManager.vue";
import CalcWorkflowMain from "../calcWorkflow/containers/CalcWorkflowMain.vue";
import StandardSettingsPanel from "../standard/containers/StandardSettingsPanel.vue";
import AuditLogMain from "../audit-log/containers/AuditLogMain.vue";
import NotificationMain from "../notification/containers/NotificationMain.vue";
import EmissionCalcResultPanel from "../emissionCalcResult/EmissionCalcResultPanel.vue";
import CarbonButton from "../common/CarbonButton.vue";

export default {
    name: "SettingsModal",
    components: {
        CarbonButton,
        StandardSettingsPanel,
        AuditLogMain,
        NotificationMain,
        BoundaryWizard,
        ActivityDataViewer,
        TargetMain,
        FactorMain,
        AccessControlManager,
        ActivityConfigManager,
        WorkflowManager,
        CalcWorkflowMain,
        EmissionCalcResultPanel,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        loginUser: {
            type: Object,
            required: true,
        },
        initialMenu: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            activeMenu: "standard",
            emissionCalcResultHasItems: false,
            settingsMenuGroups: [
                {
                    title: "기본 정보 설정",
                    menus: [
                        {
                            key: "standard",
                            label: "기준 정보",
                            icon: "fas fa-cog",
                        },
                        {
                            key: "access",
                            label: "사용자",
                            icon: "fas fa-user-shield",
                        },
                    ],
                },
                {
                    title: "배출 기준 설정",
                    menus: [
                        {
                            key: "boundary",
                            label: "배출 경계",
                            icon: "fas fa-sitemap",
                        },
                        {
                            key: "config",
                            label: "배출원",
                            icon: "fas fa-cog",
                        },
                        {
                            key: "factor",
                            label: "배출 계수",
                            icon: "fas fa-database",
                        },
                        {
                            key: "target",
                            label: "배출 목표",
                            icon: "fas fa-bullseye",
                        },
                    ],
                },
                {
                    title: "배출량 산정",
                    menus: [
                        {
                            key: "calcWorkflow",
                            label: "산정 워크플로우",
                            icon: "fas fa-project-diagram",
                        },
                        {
                            key: "activity",
                            label: "활동자료 조회",
                            icon: "fas fa-upload",
                        },
                        {
                            key: "emissionCalcResult",
                            label: "배출량 산정 내역",
                            icon: "fas fa-table",
                        },
                    ],
                },
                {
                    title: "시스템 관리",
                    menus: [
                        {
                            key: "notification",
                            label: "알림",
                            icon: "fas fa-bell",
                        },
                        {
                            key: "auditLog",
                            label: "감사 로그",
                            icon: "fas fa-history",
                        },
                        // {
                        //     key: "workflow",
                        //     label: "승인 관리",
                        //     icon: "fas fa-tasks",
                        // },
                    ],
                },
            ],
        };
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                document.body.style.overflow = "hidden";
                if (this.initialMenu) {
                    this.activeMenu = this.initialMenu;
                }
            } else {
                document.body.style.overflow = "";
            }
        },
    },
    beforeDestroy() {
        document.body.style.overflow = "";
    },
    methods: {
        hasUnsavedPermissionChanges() {
            return this.activeMenu === "access" && this.$refs.accessControlRef?.hasChanges?.();
        },
        async handleClose() {
            if (this.hasUnsavedPermissionChanges()) {
                const ok = await this.$bvModal.msgBoxConfirm(
                    "권한 매트릭스에 저장하지 않은 변경사항이 있습니다. 그래도 닫으시겠습니까?",
                    { title: "저장하지 않은 변경사항", okTitle: "닫기", cancelTitle: "취소" },
                );
                if (!ok) return;
            }
            this.$emit("close");
        },
        async handleNavClick(menuKey) {
            if (menuKey === this.activeMenu) return;
            if (this.hasUnsavedPermissionChanges()) {
                const ok = await this.$bvModal.msgBoxConfirm(
                    "권한 매트릭스에 저장하지 않은 변경사항이 있습니다. 그래도 이동하시겠습니까?",
                    { title: "저장하지 않은 변경사항", okTitle: "이동", cancelTitle: "취소" },
                );
                if (!ok) return;
            }
            this.activeMenu = menuKey;
        },
        handleBoundarySaved(boundary) {
            this.$emit("boundary-saved", boundary);
        },
        handleTargetSaved() {
            this.$emit("target-saved");
        },
        openCalcWorkflowCreate() {
            this.$refs.calcWorkflowMain?.openCreateWorkflowModal?.();
        },
        onEmissionCalcResultItemsChanged(hasItems) {
            this.emissionCalcResultHasItems = !!hasItems;
        },
        handleEmissionCalcResultExportExcel() {
            this.$refs.emissionCalcResultPanelRef?.exportExcel?.();
        },
    },
};
</script>

<style scoped>
/* 모달 오버레이 */
.settings-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1020;
    padding: 20px;
}

/* 모달 컨테이너 */
.settings-modal-container {
    width: 90%;
    height: 93vh;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
}

/* 헤더 */
.settings-modal-header {
    padding: 18px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    background: #f8fafc;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
}

.header-title i {
    font-size: 20px;
    color: #52b788;
}

.close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #64748b;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-btn:hover {
    background: #e2e8f0;
    color: #1e293b;
}

.close-btn i {
    font-size: 18px;
}

/* 모달 컨텐츠 */
.settings-modal-content {
    display: flex;
    flex: 1;
    min-height: 0;
}

/* 좌측 사이드바 */
.settings-sidebar {
    width: 200px;
    background: #f8fafc;
    border-right: 1px solid #e2e8f0;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
}

.settings-nav {
    padding: 12px 0;
    display: flex;
    flex-direction: column;
}

.settings-nav-group-header {
    padding: 12px 20px 8px;
    font-size: 11px;
    font-weight: 700;
    color: #bdbdbd;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.settings-nav-group-header:first-child {
    margin-top: 0;
}

.settings-nav-divider {
    height: 1px;
    background: #e2e8f0;
    margin: 8px 16px;
}

.settings-nav-item {
    width: 100%;
    padding: 11px 20px;
    background: transparent;
    border: none;
    border-left: 3px solid transparent;
    color: #475569;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: left;
}

.settings-nav-item i {
    font-size: 15px;
    width: 18px;
    text-align: center;
}

.settings-nav-item:hover {
    background: #e2e8f0;
    color: #1e293b;
}

.settings-nav-item.active {
    background: #ffffff;
    border-left-color: #52b788;
    color: #52b788;
    font-weight: 600;
}
.settings-nav-item:focus {
    outline: none;
}

/* 우측 메인 */
.settings-main {
    flex: 1;
    min-width: 0;
    background: #ffffff;
    position: relative;
}

.settings-panel {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.panel-header {
    padding: 14px 16px;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
    background: #f8fafc;
}

.panel-header-with-action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.panel-header-left {
    flex: 1;
    min-width: 0;
}

.btn-create-workflow {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    background: #52b788;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.btn-create-workflow:hover {
    background: #40916c;
    transform: translateY(-1px);
}

.btn-create-workflow i {
    font-size: 12px;
}

.panel-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.panel-desc {
    font-size: 12px;
    color: #64748b;
    margin: 0;
}

.panel-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0;
}

/* 스크롤바 스타일링 */
.panel-body::-webkit-scrollbar {
    width: 8px;
}

.panel-body::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.panel-body::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.panel-body::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* 애니메이션 */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.25s ease;
}

.modal-fade-enter-active .settings-modal-container,
.modal-fade-leave-active .settings-modal-container {
    transition:
        transform 0.25s ease,
        opacity 0.25s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter .settings-modal-container,
.modal-fade-leave-to .settings-modal-container {
    transform: scale(0.95);
    opacity: 0;
}

/* 반응형 */
@media (max-width: 1200px) {
    .settings-modal-container {
        max-width: 95%;
    }
}

@media (max-width: 768px) {
    .settings-modal-overlay {
        padding: 0;
    }

    .settings-modal-container {
        max-width: 100%;
        height: 100vh;
        border-radius: 0;
    }

    .settings-sidebar {
        width: 160px;
    }

    .settings-nav-item {
        padding: 10px 14px;
        font-size: 12px;
    }

    .panel-header {
        padding: 16px 20px 12px;
    }

    .panel-body {
        padding: 16px 20px;
    }
}
</style>
